/**
 * 🔴 **LAYER 5 OF THE GATE — `<module>:<action>` ACCESS SCOPES** (D41).
 *
 * `docs/12-security-model.md` writes out Metaship's six layers. This is the fifth: what a person may
 * DO, as opposed to what they may SEE.
 *
 * **Two different questions, deliberately kept apart.** Pinksy already has `VisibilityScope`
 * (`budget.detail`, `time.entries` — D21), which answers *what may this viewer see of a production*
 * and is granted per production by a share. This answers *what may this user do in this
 * organisation*, and is granted by their role. A person can hold `costs:write` and still see nothing
 * of a production they were never shared: both have to say yes.
 *
 * The shape is Metaship's, exactly — `<module>:<action>`, with `read ⊂ write ⊂ admin`, because a
 * screen ported from there asks for the scope by name.
 */

/** The actions, weakest first. Holding a stronger one implies the weaker. */
export type ScopeAction = "read" | "write" | "admin";
export const SCOPE_ACTIONS: ReadonlyArray<ScopeAction> = ["read", "write", "admin"];

/**
 * 🔴 The rank, and why it is a function rather than a `MAX()`.
 *
 * Metaship computes a module's access as the maximum across the routes a user's roles grant, and its
 * source carries a warning worth repeating: it must be computed by RANK
 * (`ELT(MAX(FIELD(access,'none','read','write','admin')), …)`) and **not** a bare `MAX(access)` —
 * MySQL aggregates an ENUM lexicographically, so `MAX('read','admin')` returns `'read'` and silently
 * DOWNGRADES every module that mixes admin- and read-level grants. Ours ranks in TypeScript, where
 * the ordering is the array above and cannot be got wrong by the database.
 */
export const actionRank = (action: ScopeAction): number => SCOPE_ACTIONS.indexOf(action);

/**
 * 🔴 **THE AUTHORITY VOCABULARY — WHICH IS NOT THE MODULE LIST**, and the difference is the bug this
 * comment used to be.
 *
 * It read *"the modules the Desk is made of — the sidebar's own list (D32)"*, and on 2026-09-13 that
 * was false in both directions at once. Verified against `modules/<name>` on disk and against
 * `tenantAppModules` on the shared dev database, the Desk ships **eleven** modules — admin ·
 * analytics · artists · costs · deals · management · organisations · people · productions · profile ·
 * time. This list names ten. Four of the eleven are not in it, and three of its own members are not
 * modules.
 *
 * Neither half is drift to be tidied away, which is why nothing is added or removed here:
 *
 *   · **A scope is an AUTHORITY.** `slate:*` guards the twenty-two schedule endpoints that live
 *     inside `@pinksy/productions`; `billing:*` guards the account panes inside `@pinksy/management`.
 *     An authority does not need a directory named after it — see `SCOPES_WITHOUT_A_MODULE`.
 *   · **A module is a PLACE.** `@pinksy/profile` is the person's own record and every endpoint behind
 *     it is `PUBLIC` by decision; `@pinksy/analytics` must never carry a scope of its own, because
 *     `services/auth/endpoint-scopes.ts` says in as many words that one *"would be a door round every
 *     other scope in this file"*. A place does not need an authority named after it — see
 *     `MODULES_WITHOUT_A_SCOPE`.
 *
 * What was actually missing is the statement of which is which. Without it a module added tomorrow is
 * silently unclassifiable, the sidebar cannot tell "no scope" from "a scope nobody granted", and the
 * two lists drift apart with nothing failing. Those two records below are that statement, and
 * `access-scopes.test.ts` reconciles both of them against the real `modules/<name>` directories —
 * a hand-typed list is how this drifted in the first place.
 */
export type AccessModule =
  | "productions"
  | "slate"
  | "people"
  | "organisations"
  | "deals"
  | "time"
  | "costs"
  | "billing"
  | "admin";

/**
 * 🔴 **WHAT A MODULE IS CALLED WHEN THE API IS SHOWN BY MODULE** (D129). One label per authority, used
 * by the API description a caller is served and by the Desk's API Docs pane, so the two never say two
 * things. `account` is not a module: it is the group for the endpoints that need no scope because
 * their subject is the caller (sign-in, the session, the person's own record).
 */
export const MODULE_LABELS: Readonly<Record<AccessModule | "account", string>> = {
  account: "Your account",
  productions: "Productions",
  slate: "Schedule",
  people: "People",
  organisations: "Organisations & contacts",
  deals: "Deals & engagements",
  time: "Time",
  costs: "Costs & actuals",
  billing: "Billing",
  admin: "Platform (operator)",
};

export const ACCESS_MODULES: ReadonlyArray<AccessModule> = [
  "productions",
  // 🔴 **AN AUTHORITY THAT OUTLIVED ITS PLACE, AND IS STILL LOAD-BEARING.** Migration 040 deleted the
  // `/slate` tile and the `@pinksy/slate` rows because that module was never built. The SCOPE stayed,
  // and it had to: `slate:read`/`slate:write` guard the twenty-two schedule endpoints — scenes,
  // characters, casting, shoot days, call sheets — which are served inside `@pinksy/productions` and
  // reached from `/productions?section=scenes`. Deleting the name here fails
  // `endpoint-scopes.test.ts`'s `unknownScopes()` twenty-two times over and drops three live
  // `roleAccesses` rows (owner · administrator · member) on the floor. See `SCOPES_WITHOUT_A_MODULE`.
  "slate",
  "people",
  "organisations",
  "deals",
  "time",
  "costs",
  // 🔴 **THE COMPANY'S ACCOUNT WITH PINKSY** (D104) — separate from `costs` on purpose. `costs` is
  // what a SHOW spends; this is what a COMPANY owes US. A production accountant reading a budget has
  // no business in the platform's own ledger, and a finance officer topping the account up has no
  // business in a production's actuals.
  "billing",
  "admin",
];

/**
 * 🔴 **AN AUTHORITY WITH NO PLACE OF ITS OWN — and the sentence that has to be true for it to stay.**
 *
 * One entry per `ACCESS_MODULES` member that is not a `modules/<name>` directory. The value is read by
 * a person, never by code: it is the argument that this name is an authority rather than a leftover,
 * and `access-scopes.test.ts` refuses an empty one — the same bargain `PUBLIC(because)` strikes in
 * `services/auth/endpoint-scopes.ts`, for the same reason. A scope that guards nothing and cannot say
 * why is a scope somebody will grant by mistake.
 *
 * The test also refuses a name that IS a module and a member missing from here entirely, so this
 * cannot quietly go stale in either direction.
 */
export const SCOPES_WITHOUT_A_MODULE: Readonly<Record<string, string>> = {
  slate:
    "the SCHEDULE's authority — scenes, characters, casting, shoot days and call sheets, twenty-two endpoints served inside @pinksy/productions and reached from /productions?section=scenes. Migration 040 deleted the /slate tile; the endpoints it guards never went anywhere.",
  billing:
    "the company's account with Pinksy (D104) — the panes inside @pinksy/management at /management?section=billing, plus the operator's settlement screens. Deliberately not `costs`: `costs` is what a SHOW spends, this is what a COMPANY owes us.",
};

/**
 * 🔴 **A PLACE WITH NO AUTHORITY OF ITS OWN — and why inventing one would be the wrong fix.**
 *
 * One entry per `modules/<name>` directory absent from `ACCESS_MODULES`. Every one of these is a
 * DECISION with a paper trail, not an oversight, which is the finding that stopped this change from
 * simply adding four members to the union: a scope no endpoint requires and no role grants is dead on
 * arrival, and the sidebar would hide the row from everybody the moment it could classify it (D54).
 *
 * Where a module is nonetheless gated, it is gated through somebody else's authority — the one its own
 * first read already requires. `apps/shell/src/nav/navScopes.ts` holds that route-root map, because
 * D115 puts the Desk's sitemap in the shell and never in a shared package.
 */
export const MODULES_WITHOUT_A_SCOPE: Readonly<Record<string, string>> = {
  analytics:
    "🔴 MUST NEVER HAVE ONE. `services/auth/endpoint-scopes.ts`: an `analytics:read` \"would be a door round every other scope in this file — permission to open a dashboard turned into permission to read payroll\". Every /analytics endpoint takes `organisations:read`, and each DATASET is re-gated on its own scope inside the handler.",
  artists:
    "the agency's own roster (D92, D80). Its first read is GET /organisations/:id/people and its writes are the /talent pair, so `organisations:read` and `people:write` already say everything an `artists:*` would have said.",
  management:
    "every company has one, by decision — migration 030 (D90): \"there is no scope carved for it… navScopes.ts leaves the row alone\". Each pane's endpoints are gated server-side as they always were, and they are gated on THIRTEEN different scopes, which is exactly why the place itself carries none.",
  payments:
    "paying suppliers, crew and cast (PNK-246, project P-PNK-118) — money a SHOW spends, so it is gated as `costs` (`navScopes.ts` maps `/payments` to it) and its rail rows on `cost.detail` / `cost.approvals`. Phase 1 has ONE endpoint, the AI read of an uploaded invoice (`POST /productions/:id/payments/invoices/read`, `costs:write`), shut server-side unless `PINKSY_PAYMENTS` is on; whether payments ever needs an authority of its own is PNK-245's question for Devin.",
  profile:
    "a person's own record (D67), reached from the account menu. Every endpoint behind it is /me/… and PUBLIC by decision, and the register says why in terms that settle this one: \"they must NOT be organisation-scoped… scoping it would make it belong to whichever company happened to be selected in the sidebar\". A person who has joined no company still has a profile.",
};

/** `productions:read`, `costs:write`, `admin:admin`. */
export type AccessScope = `${AccessModule}:${ScopeAction}`;

export const scopeOf = (module: AccessModule, action: ScopeAction): AccessScope => `${module}:${action}`;

/** Every scope that exists. Used to reject a typo'd requirement at the call rather than at runtime. */
export const ALL_ACCESS_SCOPES: ReadonlyArray<AccessScope> = ACCESS_MODULES.flatMap((module) =>
  SCOPE_ACTIONS.map((action) => scopeOf(module, action)),
);

export const isAccessScope = (value: unknown): value is AccessScope =>
  typeof value === "string" && (ALL_ACCESS_SCOPES as ReadonlyArray<string>).includes(value);

/** Split a scope. Returns null for anything malformed rather than guessing. */
export const parseScope = (scope: string): { module: AccessModule; action: ScopeAction } | null => {
  const [module, action] = scope.split(":");
  if (module === undefined || action === undefined) return null;
  if (!(ACCESS_MODULES as ReadonlyArray<string>).includes(module)) return null;
  if (!(SCOPE_ACTIONS as ReadonlyArray<string>).includes(action)) return null;
  return { module: module as AccessModule, action: action as ScopeAction };
};

/**
 * 🔴 **`read ⊂ write ⊂ admin`** — holding a stronger action implies every weaker one ON THAT MODULE.
 *
 * The implication is expanded ONCE, here, rather than checked at every call site. A guard that asked
 * "do you hold exactly `costs:read`?" would refuse an administrator, and the fix people reach for is
 * `requireAnyScope(["costs:read", "costs:write", "costs:admin"])` at every call — which is three
 * chances to forget one.
 */
export const impliedScopes = (granted: AccessScope): ReadonlyArray<AccessScope> => {
  const parsed = parseScope(granted);
  if (parsed === null) return [];
  const rank = actionRank(parsed.action);
  return SCOPE_ACTIONS.filter((action) => actionRank(action) <= rank).map((action) => scopeOf(parsed.module, action));
};

/** Expand a whole grant into everything it implies, deduped. */
export const expandScopes = (granted: ReadonlyArray<AccessScope>): ReadonlyArray<AccessScope> => [
  ...new Set(granted.flatMap(impliedScopes)),
];

/**
 * 🔴 **WHAT EACH ROLE GRANTS**, and the one rule that is not obvious.
 *
 * An organisation `owner` and `administrator` get `admin` on everything EXCEPT `admin` itself, which
 * is the platform's own module (D34: platform authority is the operator secret, never a row). A
 * company administrator administers their company; they do not administer Pinksy.
 *
 * A production role grants nothing at organisation level — a producer on one production is not
 * thereby able to read the company's payroll. What a production role grants is VISIBILITY, which is
 * `PRODUCTION_ROLE_SCOPES` in `roles.ts` and a different question entirely.
 */
export const ORGANISATION_ROLE_SCOPES: Readonly<Record<string, ReadonlyArray<AccessScope>>> = {
  owner: ACCESS_MODULES.filter((m) => m !== "admin").map((m) => scopeOf(m, "admin")),
  administrator: ACCESS_MODULES.filter((m) => m !== "admin").map((m) => scopeOf(m, "admin")),
  // A member reads the shared surfaces and writes nothing by default; a production role is what
  // gives them work to do, and a share is what lets them see it.
  member: ["productions:read", "slate:read", "people:read", "organisations:read"],
  /**
   * 🔴 **AN AGENT LOOKS AFTER PEOPLE, AND NOTHING ELSE** (D79, D82, D106).
   *
   * `people:admin` is the whole point — their artists ARE people, and `admin` implies write implies
   * read. `organisations:write` because keeping the company's own directory is the agent's job and
   * `read` cannot do it (D82: an agent with `organisations:read` searched the registry, found the
   * company, pressed Add and was refused). What `write` does NOT open is another company's record —
   * `assertOwnCompanyOrOperator` checks the company being edited is the one the caller acts in,
   * because a scope says WHAT a caller may do and never TO WHOM.
   *
   * 🔴 Two entries, not three: `read ⊂ write`, so `organisations:read` is what `expandScopes` adds
   * and spelling it out here would be a third place to keep in step. This is exactly the list
   * `tools/import-agency.ts` reconciles onto an agency's own copy, so the template and the importer
   * cannot disagree.
   *
   * 🔴 **`deals:write`, because gate 1 IS the agent's job** (D164c). Devin: *"The agent will send it
   * through to her, there will be a double gate."* The agency reads its artists' deals
   * (`GET /organisations/:id/deals`), counters the production and sends the standing version through
   * (`POST /engagements/:id/forward`) — and every one of those handlers ALSO requires the caller's
   * company to represent the person on THAT deal's chain, so the scope opens her deals and nobody
   * else's. It opens no production: every other deals door checks standing on the production, which an
   * agency does not have. It never signs, accepts or executes — those refuse an agency by name.
   *
   * Nothing else. No `costs`, no `billing` — an agent who needs to read a production's budget is a
   * different conversation, and it should be one. (`payroll` stood in this sentence until it was
   * retired; pay is `costs`, which is already refused here.)
   */
  agent: ["people:admin", "organisations:write", "deals:write"],
};

/** The scopes an organisation role grants. Unknown role → nothing, never everything. */
export const scopesForOrganisationRole = (role: string): ReadonlyArray<AccessScope> =>
  expandScopes(ORGANISATION_ROLE_SCOPES[role] ?? []);

/**
 * 🔴 **PLATFORM AUTHORITY — Metaship's `tagId`, brought in as-is** (D42).
 *
 * `1` = admin (superadmin) · `2` = system-admin · anything else = an ordinary user.
 *
 * Derived SERVER-SIDE from the user row at validation time, never read from a token's own scopes
 * claim — Metaship's comment on the same function says why: *"to ensure scopes are always current
 * with the token's tagId, and to backfill scopes for tokens issued before scope support was
 * added."* An older token, or a tampered one, cannot carry admin it was not granted.
 */
export const PLATFORM_ADMIN_TAG = 1;
export const PLATFORM_SYSTEM_ADMIN_TAG = 2;

/** The scopes that pass every check. Metaship's `ADMIN_BYPASS_SCOPES`, same names. */
export const ADMIN_BYPASS_SCOPES: ReadonlyArray<string> = ["admin", "system-admin"];

export const scopesForTagId = (tagId: number | null | undefined): ReadonlyArray<string> => {
  if (tagId === PLATFORM_ADMIN_TAG) return ["admin", "system-admin"];
  if (tagId === PLATFORM_SYSTEM_ADMIN_TAG) return ["system-admin"];
  return [];
};

/**
 * 🔴 Does this scope set carry platform authority?
 *
 * The bypass Metaship's guard applies: a token carrying `admin` or `system-admin` passes every scope
 * check. Without it a plain `includes()` wrongly 403s the operators who legitimately have full
 * access — which is the whole reason the branch exists.
 */
export const hasAdminBypass = (scopes: ReadonlyArray<string>): boolean =>
  scopes.some((scope) => ADMIN_BYPASS_SCOPES.includes(scope));

/**
 * 🔴 **THE PLATFORM MODULE — the only thing platform authority opens** (D50).
 *
 * Devin: *"as an admin, we don't just have access straight. We add ourselves to an organization…
 * we run it from inside."*
 *
 * D42 brought Metaship's bypass in as-is, and as-is it passed EVERY check — so an operator who stood
 * on a company read its money without holding `costs:read`, and the roster that company reads showed
 * a support affiliation whose actual reach was "everything". That is the one thing the standing-on
 * design exists to make honest, so the bypass is narrowed to the module it is about.
 *
 * `admin:*` is the operator's own module and no organisation role grants it (`scope-guard.ts`), so
 * this is the whole of platform authority: an operator opens the platform screens by being an
 * operator, and opens a COMPANY by joining it and holding what that membership grants. Membership
 * itself was never bypassable — layers 2 and 3 have no branch for `tagId` at all.
 *
 * This is a deliberate divergence from Metaship, and the only one in the security model.
 */
export const PLATFORM_MODULE: AccessModule = "admin";

export const isPlatformScope = (scope: string): boolean => scope.startsWith(`${PLATFORM_MODULE}:`);
